import React, { useState, useEffect } from 'react';
import { CATALOG, CATEGORIES } from './integrations-catalog';
import './integrations.scss';
import {
  SiSplunk,
  SiElastic,
  SiVmware,
  SiPaloaltonetworks,
  SiFortinet,
  SiCisco,
  SiGooglecloud,
  SiVirustotal,
  SiOkta,
  SiCloudflare,
  SiKubernetes,
  SiGithub,
  SiSlack,
  SiAtlassian,
  SiNewrelic,
  SiSalesforce,
} from 'react-icons/si';
import {
  Search,
  CheckCircle,
  AlertCircle,
  Settings,
  Link2,
  Link2Off,
  RefreshCw,
  Clock,
  X,
  Eye,
  EyeOff,
  Save,
  ExternalLink,
  Zap,
  Info,
  Wifi,
  Webhook,
  Key,
  Server,
  ChevronRight,
  Loader2,
  ShieldCheck,
  ShieldAlert,
  BookOpen,
} from 'lucide-react';

// ─── Ingest Method Helpers ─────────────────────────────────────────────────────

const INGEST_METHODS = ['All', 'API', 'OAuth', 'Syslog'];

function deriveIngestMethod(integration) {
  const allTitles = integration.fieldGroups.map((g) => g.title.toLowerCase()).join(' ');
  const allKeys   = integration.fieldGroups.flatMap((g) => g.fields.map((f) => f.key)).join(' ');
  if (allTitles.includes('syslog') || allTitles.includes('cef') || allKeys.includes('syslog')) return 'Syslog';
  if (allTitles.includes('oauth') || allKeys.includes('client_id')) return 'OAuth';
  return 'API';
}

const INGEST_ICON = {
  API:    <Wifi size={10} />,
  OAuth:  <Key size={10} />,
  Syslog: <Server size={10} />,
  Webhook:<Webhook size={10} />,
};

const INGEST_COLOR = {
  API:    { bg: 'rgba(52,211,153,0.1)',  color: '#34d399', border: 'rgba(52,211,153,0.25)' },
  OAuth:  { bg: 'rgba(96,165,250,0.1)',  color: '#60a5fa', border: 'rgba(96,165,250,0.25)' },
  Syslog: { bg: 'rgba(251,146,60,0.1)',  color: '#fb923c', border: 'rgba(251,146,60,0.25)' },
  Webhook:{ bg: 'rgba(167,139,250,0.1)', color: '#a78bfa', border: 'rgba(167,139,250,0.25)' },
};

// ─── Inline SVG brand logos (for brands without SI icons) ──────────────────────

const MicrosoftSvg = () => (
  <svg viewBox="0 0 21 21" width="55%" height="55%">
    <rect x="0"  y="0"  width="10" height="10" fill="#f25022" />
    <rect x="11" y="0"  width="10" height="10" fill="#7fba00" />
    <rect x="0"  y="11" width="10" height="10" fill="#00a4ef" />
    <rect x="11" y="11" width="10" height="10" fill="#ffb900" />
  </svg>
);

const AwsSvg = () => (
  <svg viewBox="0 0 80 48" width="70%" height="70%">
    <text x="0" y="28" fontFamily="Arial Black, sans-serif" fontWeight="900" fontSize="28" fill="#FF9900">AWS</text>
    <path d="M8 36 Q40 46 72 36" stroke="#FF9900" strokeWidth="3" fill="none" strokeLinecap="round"/>
    <polygon points="70,32 76,37 70,42" fill="#FF9900"/>
  </svg>
);

const IbmSvg = () => (
  <svg viewBox="0 0 60 24" width="65%" height="65%">
    {[0,4,8,12,16,20].map((y) => (
      <rect key={y} x="0" y={y} width="60" height="2.5" fill="#1F70C1" rx="1"/>
    ))}
    {/* I cutouts */}
    {[4, 8].map(y => <rect key={`i${y}`} x="0" y={y} width="12" height="4" fill="transparent"/>)}
    {/* B cutouts */}
    {[4, 8].map(y => <rect key={`b${y}`} x="20" y={y} width="8"  height="4" fill="transparent"/>)}
    {/* M cutouts */}
    {[4, 8].map(y => <rect key={`m${y}`} x="48" y={y} width="12" height="4" fill="transparent"/>)}
    <text x="3"  y="18" fontFamily="Arial Black" fontWeight="900" fontSize="18" fill="#1F70C1" letterSpacing="3">IBM</text>
  </svg>
);

const CrowdStrikeSvg = () => (
  <svg viewBox="0 0 40 40" width="60%" height="60%">
    <path d="M20 4 C28 4 35 10 36 18 L30 18 C29 13 25 10 20 10 C14 10 10 14 10 20 C10 26 14 30 20 30 C25 30 29 27 30 22 L36 22 C35 30 28 36 20 36 C11 36 4 29 4 20 C4 11 11 4 20 4Z" fill="#E01E35"/>
    <circle cx="23" cy="16" r="3" fill="#fff"/>
  </svg>
);

const SentinelOneSvg = () => (
  <svg viewBox="0 0 40 40" width="60%" height="60%">
    <polygon points="20,3 37,13 37,27 20,37 3,27 3,13" fill="none" stroke="#6B2BD6" strokeWidth="2.5"/>
    <polygon points="20,9 31,15 31,25 20,31 9,25 9,15" fill="#6B2BD6" opacity="0.4"/>
    <text x="11" y="25" fontFamily="Arial Black" fontWeight="900" fontSize="13" fill="#fff">S1</text>
  </svg>
);

const AwsShieldSvg = () => (
  <svg viewBox="0 0 40 40" width="62%" height="62%">
    <path d="M20 3 L35 9 L35 21 C35 29 28 35 20 37 C12 35 5 29 5 21 L5 9 Z" fill="#FF9900" opacity="0.2" stroke="#FF9900" strokeWidth="1.5"/>
    <text x="8" y="27" fontFamily="Arial Black" fontWeight="900" fontSize="13" fill="#FF9900">AWS</text>
  </svg>
);

const RecordedFutureSvg = () => (
  <svg viewBox="0 0 40 40" width="62%" height="62%">
    <circle cx="20" cy="20" r="17" fill="none" stroke="#CC2027" strokeWidth="2"/>
    <text x="9" y="26" fontFamily="Arial Black" fontWeight="900" fontSize="16" fill="#CC2027">RF</text>
  </svg>
);

const MispSvg = () => (
  <svg viewBox="0 0 40 40" width="62%" height="62%">
    <rect x="4" y="4" width="32" height="32" rx="4" fill="#E07B27" opacity="0.2" stroke="#E07B27" strokeWidth="1.5"/>
    <text x="3" y="27" fontFamily="Arial Black" fontWeight="900" fontSize="13" fill="#E07B27">MISP</text>
  </svg>
);

const ActiveDirSvg = () => (
  <svg viewBox="0 0 40 40" width="60%" height="60%">
    <rect x="4" y="4" width="32" height="32" rx="3" fill="#0072C6" opacity="0.15" stroke="#0072C6" strokeWidth="1.5"/>
    <text x="9" y="27" fontFamily="Arial Black" fontWeight="900" fontSize="16" fill="#0072C6">AD</text>
    <circle cx="20" cy="14" r="4" fill="#0072C6"/>
    <path d="M12 28 C12 22 28 22 28 28" fill="#0072C6"/>
  </svg>
);

const CyberArkSvg = () => (
  <svg viewBox="0 0 40 40" width="60%" height="60%">
    <path d="M20 4 L36 14 L36 26 L20 36 L4 26 L4 14 Z" fill="none" stroke="#D32F2F" strokeWidth="2"/>
    <text x="11" y="26" fontFamily="Arial Black" fontWeight="900" fontSize="13" fill="#D32F2F">CA</text>
  </svg>
);

// ─── Generic logo for brands without specific icons ────────────────────────────
function GenericLogo({ name = '', color = '#34d399' }) {
  const initials = name.split(/[\s\-_/]+/).map(w => w[0]).join('').toUpperCase().slice(0, 2);
  return (
    <svg viewBox="0 0 40 40" width="60%" height="60%">
      <rect x="3" y="3" width="34" height="34" rx="5" fill={color} opacity="0.15" stroke={color} strokeWidth="1.5"/>
      <text x="20" y="26" textAnchor="middle" fontFamily="'Inter', Arial Black, sans-serif"
        fontWeight="700" fontSize={initials.length > 1 ? '15' : '18'} fill={color}>{initials}</text>
    </svg>
  );
}

// ─── Brand icon map ────────────────────────────────────────────────────────────
const BRAND_MAP = {
  // SIEM
  'microsoft-sentinel':   { bg: '#0a1628', border: '#0078D4', SvgIcon: MicrosoftSvg },
  'splunk':               { bg: '#0a0f0a', border: '#65A637', SiIcon: SiSplunk,           iconColor: '#65A637' },
  'splunk-soar':          { bg: '#0a0f0a', border: '#65A637', SiIcon: SiSplunk,           iconColor: '#65A637' },
  'qradar':               { bg: '#0a1020', border: '#1F70C1', SvgIcon: IbmSvg },
  'elastic-siem':         { bg: '#1a1500', border: '#F0BF00', SiIcon: SiElastic,          iconColor: '#F0BF00' },
  // Endpoint
  'crowdstrike':          { bg: '#1a0608', border: '#E01E35', SvgIcon: CrowdStrikeSvg },
  'crowdstrike-telemetry':{ bg: '#1a0608', border: '#E01E35', SvgIcon: CrowdStrikeSvg },
  'crowdstrike-mobile':   { bg: '#1a0608', border: '#E01E35', SvgIcon: CrowdStrikeSvg },
  'defender':             { bg: '#0a1628', border: '#0A78D4', SvgIcon: MicrosoftSvg },
  'sentinelone':          { bg: '#150920', border: '#6B2BD6', SvgIcon: SentinelOneSvg },
  'sentinelone-idr':      { bg: '#150920', border: '#6B2BD6', SvgIcon: SentinelOneSvg },
  'carbon-black':         { bg: '#0a0f20', border: '#3D6BCC', SiIcon: SiVmware,           iconColor: '#3D6BCC' },
  'palo-alto-xdr':        { bg: '#1a0c00', border: '#FA582D', SiIcon: SiPaloaltonetworks, iconColor: '#FA582D' },
  'palo-alto-cortex-datalake': { bg: '#1a0c00', border: '#FA582D', SiIcon: SiPaloaltonetworks, iconColor: '#FA582D' },
  'sophos-edr':           { bg: '#0a1620', border: '#004EA2', color: '#004EA2' },
  'trellix-edr':          { bg: '#0a0e1a', border: '#D4312C', color: '#D4312C' },
  'trellix-epo':          { bg: '#0a0e1a', border: '#D4312C', color: '#D4312C' },
  'trellix-epo-onprem':   { bg: '#0a0e1a', border: '#D4312C', color: '#D4312C' },
  'trellix-atd':          { bg: '#0a0e1a', border: '#D4312C', color: '#D4312C' },
  'withsecure-elements':  { bg: '#1a1200', border: '#E5A000', color: '#E5A000' },
  'harfanglab-edr':       { bg: '#0a1218', border: '#3A9BAD', color: '#3A9BAD' },
  'cybereason-edr':       { bg: '#160a00', border: '#FF6B00', color: '#FF6B00' },
  'bitdefender-gravityzone':{ bg: '#1a0000', border: '#D3001A', color: '#D3001A' },
  'eset-protect':         { bg: '#001a12', border: '#00B250', color: '#00B250' },
  'kaspersky-endpoint':   { bg: '#1a0008', border: '#D32F2F', color: '#D32F2F' },
  'trend-micro-apex':     { bg: '#1a0006', border: '#D71920', color: '#D71920' },
  'lookout-mobile':       { bg: '#1a1000', border: '#FFA500', color: '#FFA500' },
  'symantec-endpoint':    { bg: '#001a18', border: '#FDB813', color: '#FDB813' },
  'tanium':               { bg: '#001a0a', border: '#00A550', color: '#00A550' },
  'watchguard-epdr':      { bg: '#1a0200', border: '#C8102E', color: '#C8102E' },
  'stormshield-ses':      { bg: '#001020', border: '#003E7E', color: '#003E7E' },
  'sekoia-agent':         { bg: '#0a0014', border: '#7C3AED', color: '#7C3AED' },
  'azure-windows':        { bg: '#0a1628', border: '#0078D4', SvgIcon: MicrosoftSvg },
  'windows':              { bg: '#0a1628', border: '#0078D4', SvgIcon: MicrosoftSvg },
  'windows-log-insight':  { bg: '#0a1628', border: '#0078D4', SvgIcon: MicrosoftSvg },
  // Network
  'palo-alto-ngfw':       { bg: '#1a0c00', border: '#FA582D', SiIcon: SiPaloaltonetworks, iconColor: '#FA582D' },
  'fortinet-fortigate':   { bg: '#1a0500', border: '#EE3124', SiIcon: SiFortinet,         iconColor: '#EE3124' },
  'fortinet-fortiproxy':  { bg: '#1a0500', border: '#EE3124', SiIcon: SiFortinet,         iconColor: '#EE3124' },
  'fortinet-fortiweb':    { bg: '#1a0500', border: '#EE3124', SiIcon: SiFortinet,         iconColor: '#EE3124' },
  'cisco-secure-firewall':{ bg: '#001a22', border: '#049FD9', SiIcon: SiCisco,            iconColor: '#049FD9' },
  'cisco-ios':            { bg: '#001a22', border: '#049FD9', SiIcon: SiCisco,            iconColor: '#049FD9' },
  'cisco-nxos':           { bg: '#001a22', border: '#049FD9', SiIcon: SiCisco,            iconColor: '#049FD9' },
  'cisco-meraki':         { bg: '#001a22', border: '#049FD9', SiIcon: SiCisco,            iconColor: '#049FD9' },
  'cisco-umbrella-dns':   { bg: '#001a22', border: '#049FD9', SiIcon: SiCisco,            iconColor: '#049FD9' },
  'cisco-umbrella-proxy': { bg: '#001a22', border: '#049FD9', SiIcon: SiCisco,            iconColor: '#049FD9' },
  'cisco-umbrella-ip':    { bg: '#001a22', border: '#049FD9', SiIcon: SiCisco,            iconColor: '#049FD9' },
  'cisco-swa':            { bg: '#001a22', border: '#049FD9', SiIcon: SiCisco,            iconColor: '#049FD9' },
  'cisco-sdwan':          { bg: '#001a22', border: '#049FD9', SiIcon: SiCisco,            iconColor: '#049FD9' },
  'cloudflare':           { bg: '#0f1008', border: '#F6821F', SiIcon: SiCloudflare,       iconColor: '#F6821F' },
  'cloudflare-gateway':   { bg: '#0f1008', border: '#F6821F', SiIcon: SiCloudflare,       iconColor: '#F6821F' },
  'cloudflare-access':    { bg: '#0f1008', border: '#F6821F', SiIcon: SiCloudflare,       iconColor: '#F6821F' },
  'zscaler-zia':          { bg: '#001828', border: '#00A3E0', color: '#00A3E0' },
  'zscaler-zpa':          { bg: '#001828', border: '#00A3E0', color: '#00A3E0' },
  'f5-big-ip':            { bg: '#1a0000', border: '#E4002B', color: '#E4002B' },
  'f5-distributed-cloud': { bg: '#1a0000', border: '#E4002B', color: '#E4002B' },
  'f5-nginx':             { bg: '#091400', border: '#009900', color: '#009900' },
  'akamai-waf':           { bg: '#001a10', border: '#009BDE', color: '#009BDE' },
  'akamai-guardicore':    { bg: '#001a10', border: '#009BDE', color: '#009BDE' },
  'imperva-waf':          { bg: '#1a0600', border: '#EB4A2A', color: '#EB4A2A' },
  'barracuda-cloudgen':   { bg: '#1a0000', border: '#CC0000', color: '#CC0000' },
  'cato-networks':        { bg: '#001a18', border: '#00C8AA', color: '#00C8AA' },
  'netskope':             { bg: '#000e1a', border: '#00A1DE', color: '#00A1DE' },
  'sophos-firewall':      { bg: '#0a1620', border: '#004EA2', color: '#004EA2' },
  'check-point-ngfw':     { bg: '#1a0005', border: '#C8102E', color: '#C8102E' },
  'juniper-ngfw':         { bg: '#000e1a', border: '#005581', color: '#005581' },
  'juniper-switches':     { bg: '#000e1a', border: '#005581', color: '#005581' },
  'sonicwall-ngfw':       { bg: '#1a0800', border: '#F26522', color: '#F26522' },
  'sonicwall-sma':        { bg: '#1a0800', border: '#F26522', color: '#F26522' },
  'vectra-cognito':       { bg: '#001a0e', border: '#00B35B', color: '#00B35B' },
  'vectra-respond':       { bg: '#001a0e', border: '#00B35B', color: '#00B35B' },
  'darktrace':            { bg: '#001218', border: '#00BCD4', color: '#00BCD4' },
  'extrahop':             { bg: '#000a18', border: '#2979FF', color: '#2979FF' },
  'suricata':             { bg: '#1a0800', border: '#F4A425', color: '#F4A425' },
  'stormshield-sns':      { bg: '#001020', border: '#003E7E', color: '#003E7E' },
  // Cloud
  'aws-cloudtrail':       { bg: '#1a1000', border: '#FF9900', SvgIcon: AwsSvg, iconColor: '#FF9900' },
  'aws-guardduty':        { bg: '#1a1000', border: '#FF9900', SvgIcon: AwsSvg, iconColor: '#FF9900' },
  'aws-s3':               { bg: '#1a1000', border: '#FF9900', SvgIcon: AwsSvg, iconColor: '#FF9900' },
  'aws-vpc-flow':         { bg: '#1a1000', border: '#FF9900', SvgIcon: AwsSvg, iconColor: '#FF9900' },
  'aws-cloudfront':       { bg: '#1a1000', border: '#FF9900', SvgIcon: AwsSvg, iconColor: '#FF9900' },
  'aws-waf':              { bg: '#1a1000', border: '#FF9900', SvgIcon: AwsSvg, iconColor: '#FF9900' },
  'aws-security-hub':     { bg: '#1a1000', border: '#FF9900', SvgIcon: AwsShieldSvg },
  'azure-activity-logs':  { bg: '#0a1628', border: '#0078D4', SvgIcon: MicrosoftSvg },
  'azure-app-gateway':    { bg: '#0a1628', border: '#0078D4', SvgIcon: MicrosoftSvg },
  'azure-nsg-flow':       { bg: '#0a1628', border: '#0078D4', SvgIcon: MicrosoftSvg },
  'azure-key-vault':      { bg: '#0a1628', border: '#0078D4', SvgIcon: MicrosoftSvg },
  'azure-front-door':     { bg: '#0a1628', border: '#0078D4', SvgIcon: MicrosoftSvg },
  'azure-defender':       { bg: '#0a1628', border: '#0078D4', SvgIcon: MicrosoftSvg },
  'azure-ad-entra':       { bg: '#0a1628', border: '#0078D4', SvgIcon: MicrosoftSvg },
  'gcp-audit':            { bg: '#001222', border: '#4285F4', SiIcon: SiGooglecloud,        iconColor: '#4285F4' },
  'gcp-vpc-flow':         { bg: '#001222', border: '#4285F4', SiIcon: SiGooglecloud,        iconColor: '#4285F4' },
  'gcp-load-balancing':   { bg: '#001222', border: '#4285F4', SiIcon: SiGooglecloud,        iconColor: '#4285F4' },
  'gke':                  { bg: '#001222', border: '#4285F4', SiIcon: SiKubernetes,          iconColor: '#326CE5' },
  'kubernetes-audit':     { bg: '#001222', border: '#326CE5', SiIcon: SiKubernetes,          iconColor: '#326CE5' },
  'google-workspace':     { bg: '#001222', border: '#4285F4', SiIcon: SiGooglecloud,        iconColor: '#4285F4' },
  'gcp-scc':              { bg: '#001222', border: '#4285F4', SiIcon: SiGooglecloud,        iconColor: '#4285F4' },
  'wiz-audit':            { bg: '#001a18', border: '#00BFA5', color: '#00BFA5' },
  'wiz-issues':           { bg: '#001a18', border: '#00BFA5', color: '#00BFA5' },
  'wiz-findings':         { bg: '#001a18', border: '#00BFA5', color: '#00BFA5' },
  'wiz-threat':           { bg: '#001a18', border: '#00BFA5', color: '#00BFA5' },
  'wiz-vuln':             { bg: '#001a18', border: '#00BFA5', color: '#00BFA5' },
  'lacework':             { bg: '#001a12', border: '#00A350', color: '#00A350' },
  'github-audit':         { bg: '#0a0a0a', border: '#FFFFFF', SiIcon: SiGithub, iconColor: '#FFFFFF' },
  'new-relic':            { bg: '#001a12', border: '#00AC69', SiIcon: SiNewrelic, iconColor: '#00AC69' },
  'salesforce':           { bg: '#001428', border: '#00A1E0', SiIcon: SiSalesforce, iconColor: '#00A1E0' },
  // IAM
  'okta':                 { bg: '#00101e', border: '#007DC1', SiIcon: SiOkta,             iconColor: '#007DC1' },
  'active-directory':     { bg: '#00101e', border: '#0072C6', SvgIcon: ActiveDirSvg },
  'microsoft-intune':     { bg: '#0a1628', border: '#0078D4', SvgIcon: MicrosoftSvg },
  'cyberark':             { bg: '#1a0404', border: '#D32F2F', SvgIcon: CyberArkSvg },
  'cisco-duo':            { bg: '#001a22', border: '#049FD9', SiIcon: SiCisco, iconColor: '#049FD9' },
  'cisco-ise':            { bg: '#001a22', border: '#049FD9', SiIcon: SiCisco, iconColor: '#049FD9' },
  // Threat Intelligence
  'virustotal':           { bg: '#050e20', border: '#394ECA', SiIcon: SiVirustotal,        iconColor: '#394ECA' },
  'recorded-future':      { bg: '#1a0304', border: '#CC2027', SvgIcon: RecordedFutureSvg },
  'misp':                 { bg: '#1a0d00', border: '#E07B27', SvgIcon: MispSvg },
  'opencti':              { bg: '#001020', border: '#1565C0', color: '#1565C0' },
  // SOAR
  'jira':                 { bg: '#001028', border: '#0052CC', SiIcon: SiAtlassian, iconColor: '#0052CC' },
  'slack':                { bg: '#1a0020', border: '#4A154B', SiIcon: SiSlack, iconColor: '#E01E5A' },
};


// Flatten fieldGroups → fields list for state init / save
function flattenFields(integration) {
  return integration.fieldGroups.flatMap((g) => g.fields);
}

// ─── Pre-connected dummy state ────────────────────────────────────────────────

const INITIAL_CONNECTED = {
  'microsoft-sentinel': {
    status: 'connected',
    lastSync: '2026-05-16T18:30:00Z',
    eventsToday: 1247,
    config: { workspace_id: 'a1b2c3d4-****', primary_key: '****', tenant_id: 'ten-****', client_id: 'app-****', client_secret: '****', subscription_id: 'sub-****', resource_group: 'rg-sentinel-prod', workspace_name: 'law-sentinel-prod' },
  },
  'crowdstrike': {
    status: 'connected',
    lastSync: '2026-05-16T19:05:00Z',
    eventsToday: 83,
    config: { client_id: 'client-****', client_secret: '****', base_url: 'https://api.crowdstrike.com' },
  },
  'palo-alto': {
    status: 'error',
    lastSync: '2026-05-15T11:00:00Z',
    eventsToday: 0,
    config: { host: '10.0.0.1', api_key: '****', vsys: 'vsys1' },
  },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function ConnStatusBadge({ status }) {
  const map = {
    connected: { label: 'Connected', cls: 'intg-status--connected' },
    error:     { label: 'Error',     cls: 'intg-status--error'     },
    syncing:   { label: 'Syncing…',  cls: 'intg-status--syncing'   },
  };
  const s = map[status] || { label: status, cls: '' };
  return <span className={`intg-status ${s.cls}`}><span className="intg-status__dot" />{s.label}</span>;
}

// ─── Brand Logo ───────────────────────────────────────────────────────────────

function BrandLogo({ id, name = '', size = 44 }) {
  const b = BRAND_MAP[id];
  const fallbackColor = '#34d399';
  const style = {
    width: size,
    height: size,
    background: b ? b.bg : 'rgba(52,211,153,0.06)',
    border: `1.5px solid ${b ? b.border : fallbackColor}`,
  };
  if (b?.SiIcon) {
    const Icon = b.SiIcon;
    return <div className="intg-logo" style={style}><Icon size={size * 0.52} color={b.iconColor} /></div>;
  }
  if (b?.SvgIcon) {
    const Svg = b.SvgIcon;
    return <div className="intg-logo" style={style}><Svg /></div>;
  }
  return (
    <div className="intg-logo" style={style}>
      <GenericLogo name={name || id} color={b?.color || fallbackColor} />
    </div>
  );
}

// ─── Connect / Configure Modal ────────────────────────────────────────────────

function ConnectModal({ integration, existingConfig, onSave, onClose }) {
  const allFields = flattenFields(integration);
  const ingestMethod = deriveIngestMethod(integration);

  const [form, setForm] = useState(() => {
    const init = {};
    allFields.forEach((f) => { init[f.key] = existingConfig?.[f.key] || ''; });
    return init;
  });
  const [step, setStep]         = useState('configure'); // 'configure' | 'verify'
  const [showPwd, setShowPwd]   = useState({});
  const [tooltip, setTooltip]   = useState(null);
  const [testing, setTesting]   = useState(false);
  const [testResult, setTestResult] = useState(null); // null | 'ok' | 'fail'
  const [saved, setSaved]       = useState(false);
  const isEdit = !!existingConfig;

  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [onClose]);

  const requiredFilled = allFields.filter((f) => f.required).every((f) => form[f.key]?.trim());

  const handleTest = () => {
    setTesting(true);
    setTestResult(null);
    // Simulate a connection test (1.5 s)
    setTimeout(() => {
      setTesting(false);
      setTestResult('ok');
    }, 1500);
  };

  const handleSave = () => {
    onSave(form);
    setSaved(true);
    setTimeout(onClose, 900);
  };

  return (
    <div className="intg-modal-overlay" onClick={onClose}>
      <div className="intg-modal intg-modal--wide" onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className="intg-modal__header">
          <BrandLogo id={integration.id} name={integration.name} size={38} />
          <div className="intg-modal__header-info">
            <div className="intg-modal__header-name-row">
              <h2 className="intg-modal__title">{integration.name}</h2>
              <IngestBadge method={ingestMethod} />
            </div>
            <span className="intg-modal__vendor">{integration.vendor} · {integration.category}</span>
          </div>
          <button className="intg-modal__close" onClick={onClose}><X size={15} /></button>
        </div>

        {/* Step indicator */}
        <div className="intg-modal__steps">
          <button className={`intg-step${step === 'configure' ? ' intg-step--active' : ''}`} onClick={() => { setStep('configure'); setTestResult(null); }}>
            <span className="intg-step__num">1</span> Configure
          </button>
          <ChevronRight size={13} className="intg-step__sep" />
          <button className={`intg-step${step === 'verify' ? ' intg-step--active' : ''}${!requiredFilled ? ' intg-step--disabled' : ''}`} onClick={() => requiredFilled && setStep('verify')}>
            <span className="intg-step__num">2</span> Verify &amp; Save
          </button>
        </div>

        {/* Body */}
        <div className="intg-modal__body">
          {step === 'configure' && (
            <>
              <p className="intg-modal__desc">{integration.description}</p>

              {integration.docsUrl && (
                <a className="intg-docs-link" href={integration.docsUrl} target="_blank" rel="noopener noreferrer">
                  <ExternalLink size={12} /> View official documentation
                </a>
              )}

              {integration.fieldGroups.map((group) => (
                <div key={group.title} className="intg-modal__group">
                  <div className="intg-modal__group-header">
                    <span className="intg-modal__group-title">{group.title}</span>
                    {group.hint && <span className="intg-modal__group-hint">{group.hint}</span>}
                  </div>
                  <div className="intg-modal__fields">
                    {group.fields.map((field) => (
                      <div key={field.key} className="intg-form-field">
                        <div className="intg-form-label-row">
                          <label className="intg-form-label">
                            {field.label}
                            {field.required && <span className="intg-form-required">*</span>}
                          </label>
                          {field.tip && (
                            <div className="intg-tooltip-wrap">
                              <button className="intg-form-info-btn" type="button" onMouseEnter={() => setTooltip(field.key)} onMouseLeave={() => setTooltip(null)}>
                                <Info size={12} />
                              </button>
                              {tooltip === field.key && <div className="intg-tooltip">{field.tip}</div>}
                            </div>
                          )}
                        </div>
                        <div className="intg-form-input-wrap">
                          <input
                            className="intg-form-input"
                            type={field.type === 'password' && !showPwd[field.key] ? 'password' : 'text'}
                            placeholder={field.placeholder}
                            value={form[field.key]}
                            onChange={(e) => setForm((p) => ({ ...p, [field.key]: e.target.value }))}
                          />
                          {field.type === 'password' && (
                            <button className="intg-form-eye" onClick={() => setShowPwd((p) => ({ ...p, [field.key]: !p[field.key] }))} type="button" tabIndex={-1}>
                              {showPwd[field.key] ? <EyeOff size={13} /> : <Eye size={13} />}
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </>
          )}

          {step === 'verify' && (
            <div className="intg-verify">
              <div className="intg-verify__icon-wrap">
                <BrandLogo id={integration.id} name={integration.name} size={52} />
              </div>
              <h3 className="intg-verify__title">Test your connection</h3>
              <p className="intg-verify__desc">
                Verify that VorXOC can reach <strong>{integration.name}</strong> using the credentials you provided before saving.
              </p>

              {testResult === null && !testing && (
                <button className="intg-btn intg-btn--ghost intg-verify__test-btn" onClick={handleTest}>
                  <Wifi size={14} /> Test Connection
                </button>
              )}
              {testing && (
                <div className="intg-verify__result intg-verify__result--testing">
                  <Loader2 size={16} className="intg-spin" />
                  Testing connection to {integration.name}…
                </div>
              )}
              {testResult === 'ok' && (
                <div className="intg-verify__result intg-verify__result--ok">
                  <ShieldCheck size={16} /> Connection verified successfully
                </div>
              )}
              {testResult === 'fail' && (
                <div className="intg-verify__result intg-verify__result--fail">
                  <ShieldAlert size={16} /> Connection failed — check your credentials
                  <button className="intg-btn intg-btn--ghost intg-btn--sm" style={{marginTop:'0.5rem'}} onClick={handleTest}>Retry</button>
                </div>
              )}

              {saved && (
                <div className="intg-modal__success" style={{marginTop:'1rem'}}>
                  <CheckCircle size={14} />
                  {isEdit ? 'Configuration updated.' : 'Integration connected successfully.'}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="intg-modal__footer">
          {step === 'configure' ? (
            <>
              <button className="intg-btn intg-btn--ghost" onClick={onClose}>Cancel</button>
              <button className="intg-btn intg-btn--primary" onClick={() => setStep('verify')} disabled={!requiredFilled}>
                Next <ChevronRight size={13} />
              </button>
            </>
          ) : (
            <>
              <button className="intg-btn intg-btn--ghost" onClick={() => setStep('configure')}>Back</button>
              <button className="intg-btn intg-btn--primary" onClick={handleSave}>
                <Save size={13} />
                {isEdit ? 'Save Changes' : 'Connect Source'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Connected Card ───────────────────────────────────────────────────────────

function ConnectedCard({ integration, connection, onConfigure, onDisconnect, onSync }) {
  return (
    <div className={`intg-conn-card${connection.status === 'error' ? ' intg-conn-card--error' : ''}`}>
      <div className="intg-conn-card__left">
        <BrandLogo id={integration.id} name={integration.name} size={46} />
        <div className="intg-conn-card__info">
          <div className="intg-conn-card__name-row">
            <span className="intg-conn-card__name">{integration.name}</span>
            <ConnStatusBadge status={connection.status} />
          </div>
          <span className="intg-conn-card__vendor">{integration.vendor} · {integration.category}</span>
          <div className="intg-conn-card__meta">
            <span className="intg-meta-chip">
              <Clock size={10} />
              {connection.status === 'error' ? 'Last attempt: ' : 'Last sync: '}
              {formatDate(connection.lastSync)}
            </span>
            {connection.eventsToday > 0 && (
              <span className="intg-meta-chip">
                <Zap size={10} />
                {connection.eventsToday.toLocaleString()} events today
              </span>
            )}
          </div>
        </div>
      </div>
      <div className="intg-conn-card__actions">
        {connection.status === 'error' && (
          <span className="intg-conn-card__error-hint">
            <AlertCircle size={13} /> Check credentials
          </span>
        )}
        <button className="intg-btn intg-btn--ghost intg-btn--sm" title="Sync now" onClick={() => onSync(integration.id)}>
          <RefreshCw size={13} />
        </button>
        <button className="intg-btn intg-btn--ghost intg-btn--sm" onClick={() => onConfigure(integration)}>
          <Settings size={13} /> Configure
        </button>
        <button className="intg-btn intg-btn--ghost intg-btn--sm intg-btn--danger" onClick={() => onDisconnect(integration.id)}>
          <Link2Off size={13} /> Disconnect
        </button>
      </div>
    </div>
  );
}

// ─── Ingest Method Badge ──────────────────────────────────────────────────────

function IngestBadge({ method }) {
  const style = INGEST_COLOR[method] || INGEST_COLOR.API;
  return (
    <span className="intg-ingest-badge" style={{ background: style.bg, color: style.color, borderColor: style.border }}>
      {INGEST_ICON[method]}
      {method}
    </span>
  );
}

// ─── Catalog Card ─────────────────────────────────────────────────────────────

function CatalogCard({ integration, onConnect }) {
  const ingestMethod = deriveIngestMethod(integration);

  const handleDocsClick = (e) => {
    e.stopPropagation();
    if (integration.docsUrl) window.open(integration.docsUrl, '_blank', 'noopener');
  };

  return (
    <div className="intg-catalog-card" onClick={() => onConnect(integration)}>
      <div className="intg-catalog-card__top">
        <BrandLogo id={integration.id} name={integration.name} size={40} />
        <div className="intg-catalog-card__badges">
          <IngestBadge method={ingestMethod} />
          <span className="intg-catalog-card__cat">{integration.category}</span>
        </div>
      </div>
      <div className="intg-catalog-card__body">
        <h4 className="intg-catalog-card__name">{integration.name}</h4>
        <p className="intg-catalog-card__vendor">{integration.vendor}</p>
        <p className="intg-catalog-card__desc">{integration.description}</p>
      </div>
      <div className="intg-catalog-card__footer">
        {integration.docsUrl && (
          <button className="intg-docs-btn" onClick={handleDocsClick} type="button">
            <BookOpen size={11} /> View docs
          </button>
        )}
        <button className="intg-btn intg-btn--primary intg-btn--sm intg-catalog-card__connect-btn" onClick={(e) => { e.stopPropagation(); onConnect(integration); }}>
          <Link2 size={12} /> Connect
        </button>
      </div>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function Integrations() {
  const [connected, setConnected]   = useState(INITIAL_CONNECTED);
  const [search, setSearch]         = useState('');
  const [category, setCategory]     = useState('All');
  const [ingestFilter, setIngestFilter] = useState('All');
  const [modal, setModal]           = useState(null);

  const connectedList  = CATALOG.filter((i) => connected[i.id]);
  const availableList  = CATALOG.filter((i) => !connected[i.id]).filter((i) => {
    const q = search.toLowerCase();
    const matchQ      = !q || i.name.toLowerCase().includes(q) || i.vendor.toLowerCase().includes(q) || i.description?.toLowerCase().includes(q);
    const matchCat    = category === 'All' || i.category === category;
    const matchIngest = ingestFilter === 'All' || deriveIngestMethod(i) === ingestFilter;
    return matchQ && matchCat && matchIngest;
  });

  const handleConnect    = (intg) => setModal({ integration: intg, isEdit: false });
  const handleConfigure  = (intg) => setModal({ integration: intg, isEdit: true  });

  const handleSave = (config) => {
    if (!modal) return;
    setConnected((prev) => ({
      ...prev,
      [modal.integration.id]: {
        status: 'connected',
        lastSync: new Date().toISOString(),
        eventsToday: prev[modal.integration.id]?.eventsToday || 0,
        config,
      },
    }));
  };

  const handleDisconnect = (id) => {
    setConnected((prev) => { const n = { ...prev }; delete n[id]; return n; });
  };

  const handleSync = (id) => {
    setConnected((prev) => ({ ...prev, [id]: { ...prev[id], status: 'syncing', lastSync: new Date().toISOString() } }));
    setTimeout(() => {
      setConnected((prev) => ({ ...prev, [id]: { ...prev[id], status: 'connected' } }));
    }, 2200);
  };

  return (
    <div className="integrations">
      {modal && (
        <ConnectModal
          integration={modal.integration}
          existingConfig={modal.isEdit ? connected[modal.integration.id]?.config : null}
          onSave={handleSave}
          onClose={() => setModal(null)}
        />
      )}

      {/* ── Connected ── */}
      <div className="integrations__section">
        <div className="integrations__section-header">
          <h3 className="integrations__section-title">
            <CheckCircle size={16} />
            Connected Integrations
            <span className="integrations__count">{connectedList.length}</span>
          </h3>
        </div>
        {connectedList.length === 0 ? (
          <div className="integrations__empty">
            <Link2Off size={28} />
            <p>No integrations connected yet. Add one from the catalog below.</p>
          </div>
        ) : (
          <div className="integrations__conn-list">
            {connectedList.map((intg) => (
              <ConnectedCard
                key={intg.id}
                integration={intg}
                connection={connected[intg.id]}
                onConfigure={handleConfigure}
                onDisconnect={handleDisconnect}
                onSync={handleSync}
              />
            ))}
          </div>
        )}
      </div>

      {/* ── Catalog ── */}
      <div className="integrations__section">
        <div className="integrations__section-header">
          <h3 className="integrations__section-title">
            <Zap size={16} />
            Integration Catalog
          </h3>
          <span className="integrations__source-count">{availableList.length} <span className="integrations__source-count-label">sources</span></span>
        </div>

        <div className="integrations__toolbar">
          <div className="integrations__toolbar-top">
            <div className="intg-search">
              <Search size={14} className="intg-search__icon" />
              <input
                className="intg-search__input"
                placeholder="Search by name, vendor or description…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
          <div className="integrations__filter-row">
            <div className="intg-filter-group">
              <span className="intg-filter-label">Ingest Method</span>
              <div className="intg-cat-group">
                {INGEST_METHODS.map((m) => (
                  <button key={m} className={`intg-cat-btn${ingestFilter === m ? ' intg-cat-btn--active' : ''}`} onClick={() => setIngestFilter(m)}>{m}</button>
                ))}
              </div>
            </div>
            <div className="intg-filter-group">
              <span className="intg-filter-label">Category</span>
              <div className="intg-cat-group">
                {CATEGORIES.map((c) => (
                  <button key={c} className={`intg-cat-btn${category === c ? ' intg-cat-btn--active' : ''}`} onClick={() => setCategory(c)}>{c}</button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {availableList.length === 0 ? (
          <div className="integrations__empty">
            <CheckCircle size={28} />
            <p>All integrations in this category are already connected.</p>
          </div>
        ) : (
          <div className="integrations__catalog-grid">
            {availableList.map((intg) => (
              <CatalogCard key={intg.id} integration={intg} onConnect={handleConnect} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
